/* eslint-disable no-cond-assign */

import moment from 'moment';

import { ProposalDAOInterface } from '../ProposalDAO';
import { ComparisonItemBasedProposalDAO } from '../ProposalDAOImpl';
import * as ProposalNS from '../../Proposal';
import * as ProposalElementNS from '../../ProposalElements';
import { ProposalFieldDAO } from '../ProposalFieldDAO';
import { ComparisonItemDAO } from '../ProposalItemDAO';
import { DataPointResponse, DataPoints, ResponseHasDisclaimer } from '../DataPoints';
import { ProposalStyleClassNames } from '../../ProposalStyleClassNames';
import { NotApplicableCell, GrowthCell } from '../../../../views/components/cells';
import { getLastNYears } from '../../../../utils/time';
import Dictionary from '@/types/dictionary';
import ProposalToolbarOption from '@/types/proposal-toolbar-option';
import SeriesData from '@/types/series-data';

export default class CalendarYearReturnsDAO extends ComparisonItemBasedProposalDAO {
  public static buildInstance(proposal: ProposalNS.Proposal, proposalElement: ProposalElementNS.ProposalDataElement): ProposalDAOInterface {
    return new CalendarYearReturnsDAO(proposal, proposalElement);
  }

  years: Array<string>;

  toolbarOption: ProposalToolbarOption | undefined;

  constructor(proposal: ProposalNS.Proposal, proposalElement: ProposalElementNS.ProposalDataElement) {
    super(proposal, proposalElement);
    this.toolbarOption = {};
    this.years = [];
  }

  /* eslint-disable-next-line class-methods-use-this */
  protected getColorKeys(): string[] {
    return [];
  }

  protected build(): void {
    let filterCount: number = -1;

    // test for options
    const daoConfig: ProposalNS.CalendarYearReturnsConfig = this.proposalElement.DAO as ProposalNS.CalendarYearReturnsConfig;

    if (daoConfig.filterTopXDAOOptions !== undefined) {
      filterCount = daoConfig.filterTopXDAOOptions.count;
    }

    let toolbarCount: number = -1;
    this.toolbarOption = this.getToolbarOption();

    if (filterCount === -1 && this.toolbarOption && this.toolbarOption.startDate && this.toolbarOption.endDate) {
      toolbarCount = moment().diff(this.toolbarOption.startDate, 'years');
    }

    let count: number = 10;

    if (toolbarCount !== -1 && filterCount !== -1) {
      count = Math.min(toolbarCount, filterCount);
    } else if (toolbarCount !== -1) {
      count = toolbarCount;
    } else if (filterCount !== -1) {
      count = filterCount;
    }

    const lastYear = new Date().getUTCFullYear() - 1;
    for (let i: number = 0; i < count; i++) {
      this.years.push((lastYear - i).toString()); // Build years array for
      this.addFieldName((lastYear - i).toString(), ProposalStyleClassNames.ProposalTableContent);
    }

    let itemDAO = null;
    while ((itemDAO = this.nextItem()) !== null) {
      const dpResponse: DataPointResponse<Dictionary<ResponseHasDisclaimer<number | undefined>>> = DataPoints.getCalendarYearReturns(
        itemDAO.item,
        this.proposal.showBaseCurrencies === true,
      );
      const returns: Dictionary<ResponseHasDisclaimer<number | undefined>> = dpResponse.response;

      this.addDisclaimerKeys(dpResponse.disclaimerKeys, itemDAO.item);

      const keys: Array<number> = getLastNYears(10);

      for (let i: number = 0; i < count; i++) {
        const response: ResponseHasDisclaimer<number | undefined> = returns[keys[i]];
        const value = response != null ? response.value : undefined;
        const hasDisclaimer = response != null ? response.hasDisclaimer : undefined;

        this.addItemField(itemDAO, keys[i]?.toString() || (lastYear - i).toString(), value, hasDisclaimer);
      }
    }
  }

  public getSeriesData(): SeriesData[][] {
    return this.getItemDAOs().map(itemDAO => {
      const series: SeriesData[] = [];
      const itemSeriesData = itemDAO.getSeriesData();

      // Series data for each year to prevent weird sorting issues due to missing data
      this.years.forEach(year => {
        series.push({
          name: year,
          y: 0,
          ...(itemSeriesData.find(i => i.name === year) ?? {}),
          key: itemDAO.item._id,
        });
      });

      return series.sort((a, b) => a.name.localeCompare(b.name));
    });
  }

  public getColorsDict(): Dictionary<string> {
    return Object.fromEntries(this.getItemDAOs().map(({ item }) => [item._id, item.color ?? '']));
  }

  // private methods
  private addItemField(itemDAO: ComparisonItemDAO, name: string, value: number | undefined, hasDisclaimer?: boolean): number | undefined {
    const field: ProposalFieldDAO = new ProposalFieldDAO(name, value != null ? value.toFixed(2) : '', ProposalStyleClassNames.ProposalTableContent);
    field.cellType = value != null ? GrowthCell : NotApplicableCell;
    field.hasDisclaimer = hasDisclaimer;
    this.addFieldDAO(itemDAO, field);
    return value;
  }
}
